type Callback = (x: any) => any

// root container for the callbacks — keyed by callback so add/remove is O(1)
// instead of O(n) array scans. A Map preserves insertion order, so emit still
// fires in subscription order (matching the previous array-based behavior).
export const callbacks: {
    [key: string]: Map<Callback, NodeJS.Timeout | undefined>
} = {}

export const isCallbacksEmpty = (baseKey: string, key: string): boolean => {
    return (callbacks[baseKey + key]?.size ?? 0) < 1
}

const pubSub = {
    on: (key: string, callback: Callback) => {
        if (!callbacks[key]) {
            callbacks[key] = new Map()
        }
        callbacks[key].set(callback, undefined)
        return () => pubSub.destroy(key, callback)
    },
    destroy: (key: string, callback: Callback) => {
        const subscribers = callbacks[key]
        if (!subscribers) {
            return
        }

        // clear any pending timeout before removing
        const timeoutId = subscribers.get(callback)
        if (timeoutId) {
            clearTimeout(timeoutId)
        }

        subscribers.delete(callback)
        if (subscribers.size < 1) {
            delete callbacks[key]
        }
    },
    emit: (key: string, data: any, immediate = false) => {
        const subscribers = callbacks[key]
        if (!subscribers) {
            return
        }
        if (immediate) {
            // allow for forcing an immediate flush, making it easier to
            // ensure data is where it needs to be in certain situations.
            // snapshot first so a callback that (un)subscribes during the flush
            // can't change who receives this emit — matches the prior behavior
            // where destroy replaced the array being iterated.
            for (const callback of [...subscribers.keys()]) {
                callback(data)
            }
        } else {
            // when non-immediate we should debounce so we don't trigger
            // the callback multiple times in a row unnecessarily. no user
            // callback runs synchronously here (only timer scheduling), so we
            // can iterate in place and update each timeout id directly.
            subscribers.forEach((timeoutId, callback) => {
                clearTimeout(timeoutId)
                const newTimeoutId = setTimeout(() => callback(data), 0)
                subscribers.set(callback, newTimeoutId)
            })
        }
    },
}

type Interceptors = {
    on?(key: string, data: any): any
    off?(key: string, data: any): any
}

export const createPubSub = (prefix: string, interceptors?: Interceptors) => ({
    on: (key: string, callback: Callback, data?: any) => {
        interceptors?.on?.(key, data)
        pubSub.on(prefix + key, callback)

        return () => {
            interceptors?.off?.(key, data)
            pubSub.destroy(prefix + key, callback)
        }
    },
    destroy: (key: string, callback: Callback, data?: any) => {
        interceptors?.off?.(key, data)
        pubSub.destroy(prefix + key, callback)
    },
    emit: (key: string, data?: any, immediate = false) =>
        pubSub.emit(prefix + key, data, immediate),
})
